// The office's little sounds (设置 → 声音): short, soft, pixel-game style, synthesised in the browser with OtterBonk's
// synth (otterbonk/sfx.js renderVoices: no audio files). Two kinds, each with its own switch, under one volume:
//   提示音 (notify): someone greets me (打招呼), waves / claps / sends a heart near me (quieter the further away they are,
//     panned to their side), asks me for a 双人互动 or says yes to mine, invites me (a game, 到身边: a soft chime; a
//     会议室: a ring), a chat line from someone else. 请勿打扰 mutes them all.
//   游戏音效 (game): 五子棋 and the other tables (a stone / a piece put down, the game starting, win / lose / draw),
//     你画我猜 (a round starts, I guessed it, someone else did, time up, the word shown, the final scores),
//     狼人杀 (night, day, the vote, votes cast, the exile / the dawn, who won) and 谁是卧底 (a round, my turn to
//     describe, the vote, the reveal, the end).
// 扬声器 「关闭声音」 silences them too. With the tab hidden only what is meant for me sounds (invites, a greeting, a 双人互动 request). Nothing on the wire changes: the page plays
// from messages it already gets (app.js onMessage → sounds.msg, before the modules handle them, so it can compare
// with the last state it saw); members.js plays the invite cards' chime through sounds.invite().
// The AudioContext is made on the first tap / click / key (autoplay rules, iOS: touchend / click) and sleeps a few
// seconds after the last sound; it is the page's own, not the voice's (app.js) or OtterBonk's.
import { renderVoices, hz, tune } from '/otterbonk/sfx.js';
import { t } from '/i18n.js';

// ---------------------------------------------------------------- the patches (otterbonk/sfx.js voice() fields)
const S = {
  // notify
  greet: [{ w: 'sin', f: 520, f1: 900, h: 0.03, d: 0.05, vol: 0.4, slide: 0.05 }, { w: 'tri', f: hz('e6'), t: 0.06, h: 0.04, d: 0.28, vol: 0.38 },
    { w: 'sin', f: hz('b6'), t: 0.06, h: 0.01, d: 0.22, vol: 0.1 }],
  wave: [{ w: 'sin', f: 600, f1: 1050, h: 0.02, d: 0.05, vol: 0.4, slide: 0.05 }, { w: 'tri', f: hz('a5'), t: 0.05, h: 0.02, d: 0.16, vol: 0.25 }],
  raise: [{ w: 'sq', duty: 0.25, f: 880, f1: 1320, h: 0.02, d: 0.06, vol: 0.18, lp: 4000 }],
  clap: [0, 1, 2].map((k) => ({ w: 'noise', f: 9000, t: k * 0.11, a: 0.001, h: 0.006, d: 0.05, vol: 0.4, lp: 4200, hp: 900, seed: k + 3 })),
  heart: tune([['c6', 1], ['e6', 1], ['g6', 2]], 0.06, { w: 'tri', d: 0.16, vol: 0.3, vib: [0.3, 7, 0.05] }),
  giggle: tune([['g5', 1], ['a5', 1], ['g5', 1], ['b5', 1]], 0.07, { w: 'sq', duty: 0.25, h: 0.02, d: 0.04, vol: 0.16, lp: 3000 }),
  chat: [{ w: 'sin', f: 900, f1: 1400, h: 0.01, d: 0.05, vol: 0.3, slide: 0.03 }],
  ask: [...tune([['e5', 1], ['a5', 2]], 0.08, { w: 'tri', d: 0.18, vol: 0.35 }), { w: 'sin', f: hz('e7'), t: 0.1, h: 0.01, d: 0.15, vol: 0.06 }],
  duo: tune([['c6', 1], ['e6', 1], ['g6', 1], ['c7', 2]], 0.045, { w: 'tri', d: 0.14, vol: 0.28, vib: [0.25, 8, 0.03] }),
  invite: tune([['a5', 2], ['e6', 4]], 0.07, { w: 'tri', d: 0.3, vol: 0.38 }),
  ring: [0, 0.5].flatMap((t0) => tune([['e6', 1], ['c6', 1], ['e6', 1], ['c6', 1]], 0.06, { w: 'sq', duty: 0.5, h: 0.03, d: 0.03, vol: 0.14, lp: 2800 }, t0)),
  // game: the tables
  stone: [{ w: 'noise', f: 5000, a: 0.001, h: 0.004, d: 0.04, vol: 0.35, lp: 3200, hp: 500 }, { w: 'sin', f: 1250, f1: 700, h: 0.005, d: 0.06, vol: 0.32 },
    { w: 'tri', f: 330, h: 0.01, d: 0.05, vol: 0.2 }],
  piece: [{ w: 'noise', f: 3000, a: 0.001, h: 0.006, d: 0.05, vol: 0.35, lp: 1600, hp: 200 }, { w: 'sin', f: 520, f1: 300, h: 0.01, d: 0.08, vol: 0.4 }],
  gstart: tune([['c5', 1], ['g5', 1], ['c6', 2]], 0.07, { w: 'sq', duty: 0.25, d: 0.1, vol: 0.2, lp: 4500 }),
  win: [...tune([['c5', 1], ['e5', 1], ['g5', 1], ['c6', 1], ['e6', 3]], 0.075, { w: 'sq', duty: 0.25, d: 0.14, vol: 0.2, lp: 5000 }),
    { w: 'tri', f: hz('c4'), t: 0.3, h: 0.2, d: 0.3, vol: 0.35 }, { w: 'sin', f: hz('g6'), t: 0.32, h: 0.04, d: 0.35, vol: 0.08, vib: [0.3, 7] }],
  lose: tune([['g4', 1], ['e4', 1], ['c4', 3]], 0.14, { w: 'tri', d: 0.2, vol: 0.35 }),
  draw: tune([['e5', 1], ['c5', 2]], 0.1, { w: 'tri', d: 0.2, vol: 0.3 }),
  // game: the rounds (你画我猜, 谁是卧底)
  round: [...tune([['e5', 1], ['e5', 1]], 0.12, { w: 'sq', duty: 0.5, h: 0.02, d: 0.04, vol: 0.14, lp: 3000 }),
    { w: 'tri', f: hz('a5'), t: 0.24, h: 0.08, d: 0.25, vol: 0.32 }],
  right: [...tune([['e6', 1], ['g#6', 1], ['b6', 1], ['e7', 2]], 0.045, { w: 'sq', duty: 0.5, d: 0.1, vol: 0.18, lp: 7000 }),
    { w: 'sin', f: hz('e7'), t: 0.14, h: 0.02, d: 0.25, vol: 0.12 }],
  ding: [{ w: 'tri', f: hz('b6'), h: 0.01, d: 0.18, vol: 0.22 }],
  timeup: [{ w: 'sq', duty: 0.5, f: hz('a3'), h: 0.22, d: 0.12, vol: 0.16, lp: 1100 }, { w: 'tri', f: hz('a2'), h: 0.22, d: 0.12, vol: 0.3 }],
  turn: tune([['b5', 1], ['e6', 2]], 0.06, { w: 'sq', duty: 0.25, d: 0.08, vol: 0.18, lp: 5000 }),
  reveal: tune([['g5', 1], ['b5', 1], ['d6', 1], ['g6', 3]], 0.06, { w: 'tri', d: 0.25, vol: 0.28, vib: [0.25, 6, 0.1] }),
  // game: 狼人杀
  night: [...tune([['e5', 2], ['c5', 2], ['a4', 3]], 0.12, { w: 'tri', d: 0.28, vol: 0.3, vib: [0.2, 5, 0.1] }),
    { w: 'sin', f: hz('a3'), a: 0.2, h: 0.4, d: 0.45, vol: 0.18 }],
  day: [...tune([['c5', 1], ['e5', 1], ['g5', 1], ['c6', 3]], 0.08, { w: 'tri', d: 0.2, vol: 0.3 }),
    ...[0.45, 0.58].map((t) => ({ w: 'sin', f: 2200, f1: 3200, t, h: 0.02, d: 0.04, vol: 0.08 }))],
  vote: [0, 0.16].flatMap((t) => [{ w: 'sin', f: 300, f1: 150, t, h: 0.01, d: 0.08, vol: 0.45 }, { w: 'noise', f: 2000, t, a: 0.001, h: 0.005, d: 0.05, vol: 0.25, lp: 1400 }]),
  tick: [{ w: 'tri', f: 1200, h: 0.008, d: 0.04, vol: 0.22 }],
};
/** Each sound's kind (its switch), level in the mix, and the least time between two of it (s). */
const MIX = {
  greet: ['notify', 0.9, 0.3], wave: ['notify', 0.7, 0.25], raise: ['notify', 0.6, 0.25], clap: ['notify', 0.6, 0.35], heart: ['notify', 0.7, 0.3],
  giggle: ['notify', 0.6, 0.3], chat: ['notify', 0.45, 1.5], ask: ['notify', 0.9, 0.5], duo: ['notify', 0.7, 0.5],
  invite: ['notify', 0.9, 0.5], ring: ['notify', 0.85, 1],
  stone: ['game', 0.8, 0.05], piece: ['game', 0.8, 0.05], gstart: ['game', 0.6, 0.5], win: ['game', 0.7, 1], lose: ['game', 0.7, 1], draw: ['game', 0.6, 1],
  round: ['game', 0.6, 0.5], right: ['game', 0.75, 0.3], ding: ['game', 0.5, 0.15], timeup: ['game', 0.6, 0.5], turn: ['game', 0.7, 0.5],
  reveal: ['game', 0.6, 0.5], night: ['game', 0.6, 1], day: ['game', 0.6, 1], vote: ['game', 0.6, 0.5], tick: ['game', 0.5, 0.08],
};
export const SOUND_NAMES = Object.keys(S);
/** One sound's samples (mono Float32Array at sr), soft-clipped as OtterBonk's. Pure (the previews, a WAV in Node). */
export const renderSound = (name, sr = 44100) => renderVoices(S[name], sr);
/** Actions (emotes.js ids) with a sound: 挥手, 举爪, 鼓掌, 爱心, 比心 (the cats'), 偷笑. The rest are quiet. */
const EMOTE_SOUND = { 1: 'wave', 5: 'raise', 6: 'clap', 7: 'heart', 15: 'heart', 16: 'giggle' };
const NEAR = 2, FAR = 6.5, FAR_VOL = 0.3; // tiles: full volume this close, down to FAR_VOL at the edge of hearing (office HEAR_RELEASE)
const MAX_VOICES = 6, NAP_MS = 4000, LATE_MS = 3000;

// ---------------------------------------------------------------- settings
const KEY = 'deck.sound', DEFAULTS = { vol: 0.7, notify: true, game: true };
function readSettings() {
  let j = null; try { j = JSON.parse(localStorage.getItem(KEY) || 'null'); } catch {}
  return {
    vol: typeof j?.vol === 'number' && Number.isFinite(j.vol) ? Math.max(0, Math.min(1, j.vol)) : DEFAULTS.vol,
    notify: typeof j?.notify === 'boolean' ? j.notify : DEFAULTS.notify,
    game: typeof j?.game === 'boolean' ? j.game : DEFAULTS.game,
  };
}

// ---------------------------------------------------------------- the player
const set = readSettings();
let ctx = null, master = null, napT = 0, o = null, napped = false, napping = null; // napping: the suspend() under way
const bufs = new Map(), last = new Map();
let voices = [];
function unlock() {
  if (typeof AudioContext === 'undefined' && typeof webkitAudioContext === 'undefined') return;
  if (!ctx) {
    try {
      ctx = new (globalThis.AudioContext || globalThis.webkitAudioContext)({ latencyHint: 'interactive' });
      master = ctx.createGain(); master.gain.value = set.vol; master.connect(ctx.destination);
    } catch { ctx = master = null; return; }
    napSoon();
  }
  // a context iOS (a call) or the autoplay rules stopped: wake it on this gesture. Our own nap waits for a sound.
  if (ctx.state === 'interrupted' || (ctx.state === 'suspended' && !napped)) { ctx.resume().catch(() => {}); napSoon(); }
}
if (typeof addEventListener === 'function') for (const e of ['pointerdown', 'touchend', 'click', 'keydown']) addEventListener(e, unlock, { capture: true, passive: true });
function napSoon() {
  clearTimeout(napT);
  napT = setTimeout(() => {
    if (!ctx || ctx.state !== 'running') return;
    if (voices.some((v) => v.end > ctx.currentTime)) { napSoon(); return; }
    napped = true; const p = (napping = ctx.suspend().catch(() => {})); p.then(() => { if (napping === p) napping = null; });
  }, NAP_MS);
}
function buf(name) {
  let b = bufs.get(name);
  if (!b) { const sr = Math.min(44100, ctx.sampleRate), d = renderVoices(S[name], sr); b = ctx.createBuffer(1, d.length, sr); b.copyToChannel(d, 0); bufs.set(name, b); }
  return b;
}
const myStatus = () => o?.statusOf?.(o.me?.())?.s ?? null;
/** May `name` sound now? Its switch, 请勿打扰 (notify), the tab hidden (only invites), its gap. A preview (设置 → 声音)
 *  only needs the speaker on. */
function allowed(name, opts) {
  const m = MIX[name]; if (!m || set.vol <= 0 || o?.off?.()) return false;
  if (opts.preview) return true;
  if (!set[m[0]]) return false;
  if (m[0] === 'notify' && myStatus() === 'dnd') return false;
  if (typeof document !== 'undefined' && document.hidden && !opts.hidden) return false;
  return true;
}
/**
 * Play sound `name`. opts: vol (x its level), pan (-1..1), hidden (may sound with the tab hidden: invites, and what
 * is meant for me alone: a greeting, a 双人互动 request), preview (设置: its switches and 请勿打扰 don't apply).
 * Returns whether it played (or was scheduled to, once the context wakes).
 */
function play(name, opts = {}) {
  if (!allowed(name, opts) || !ctx) return false;
  const now = performance.now() / 1000, gap = MIX[name][2];
  if (now - (last.get(name) ?? -1e9) < gap) return false;
  last.set(name, now);
  const asked = performance.now();
  const go = () => {
    try {
      if (performance.now() - asked > LATE_MS) { napSoon(); return; } // woke too late (an iOS interruption, a slow resume): a stale sound is worse than none
      voices = voices.filter((v) => v.end > ctx.currentTime);
      if (voices.length >= MAX_VOICES) return;
      const src = ctx.createBufferSource(), g = ctx.createGain();
      src.buffer = buf(name); g.gain.value = MIX[name][1] * (opts.vol ?? 1);
      let tail = g;
      if (opts.pan && ctx.createStereoPanner) { const p = ctx.createStereoPanner(); p.pan.value = Math.max(-1, Math.min(1, opts.pan)); g.connect(p); tail = p; }
      src.connect(g); tail.connect(master); src.start();
      voices.push({ end: ctx.currentTime + src.buffer.duration });
      src.onended = () => { try { tail.disconnect(); g.disconnect(); } catch {} };
      napSoon(); sounds.onPlay?.(name, g.gain.value, opts.pan ?? 0);
    } catch {}
  };
  // napping (or suspended: our nap, autoplay, iOS): wake it first; a suspend() still under way is waited for, else the
  // sound would start into a context about to stop
  if (ctx.state === 'running' && !napping) go();
  else if (ctx.state !== 'closed') { napped = false; (napping ?? Promise.resolve()).then(() => ctx.resume()).then(go, () => {}); }
  return true;
}
/** How loud and where someone at (x, y) is, from me: {vol, pan}, or null: too far, or not on the map (yet). */
function from(p) {
  const me = o?.me?.(); if (!me || !p) return null; if (p === me) return { vol: 1, pan: 0 };
  const dx = p.x - me.x, d = Math.hypot(dx, p.y - me.y);
  if (d > FAR + 1) return null;
  return { vol: d <= NEAR ? 1 : Math.max(FAR_VOL, 1 - ((d - NEAR) / (FAR - NEAR)) * (1 - FAR_VOL)), pan: Math.max(-0.5, Math.min(0.5, dx / 8)) };
}

// ---------------------------------------------------------------- what the messages mean
const greetedAt = new Map(); // peer id -> when they greeted me (their wave is the office's: no second sound for it)
const waves = new Map(); // peer id -> a pending wave sound (it may come just before the greeting)
let tables = new Map(), G = null, W = null, Wrole = null, Sp = null, welcomeAt = 0, zone = undefined;
const asks = new Set(); // 双人互动 requests already heard (the office sends open ones again after a reconnect)
/** The huts' games: the next state is a fresh look (walked out of the hut and back: what happened meanwhile is quiet). */
const forgetHuts = () => { G = W = Sp = Wrole = null; };
/** Each second (from initSounds): did I walk out of a room (a hut)? Then forget the huts' last states. Not on the way
 *  in: the hut's state comes as I enter, and it is the one the next change is compared with. */
function watchZone() {
  const me = o?.me?.(), z = me && o.zoneAt ? o.zoneAt(me.x, me.y)?.id ?? null : null;
  if (z !== zone) { if (zone) forgetHuts(); zone = z; }
}
const myId = () => { const me = o?.me?.(); return me ? me.id : null; };
const myGid = () => { const me = o?.me?.(); return me ? me.gid ?? me.id : null; };

function emote(m) {
  const name = EMOTE_SOUND[m.emote]; if (!name || !m.ms) return;
  const me = o?.me?.(), mine = m.id === me?.id, p = mine ? me : o?.peers?.().get(m.id);
  const at = mine ? { vol: 0.5, pan: 0 } : from(p); if (!at) return;
  if (name !== 'wave' || mine) { play(name, at); return; }
  // a wave: wait a moment, it may be the greeter's (the greeting's own sound says it)
  clearTimeout(waves.get(m.id));
  waves.set(m.id, setTimeout(() => { waves.delete(m.id); if (Date.now() - (greetedAt.get(m.id) ?? 0) > 2500) play('wave', at); }, 250));
}
function gomoku(m) {
  const was = tables.get(m.table); tables.set(m.table, { phase: m.phase, moves: m.moves, kind: m.kind, game: m.game });
  if (!was) return; // walked up to it: nothing happened yet
  const k = myGid(), seat = m.seats?.[0]?.id === k ? 1 : m.seats?.[1]?.id === k ? 2 : 0, v = seat ? 1 : 0.55;
  // a game starts: the office counts it (game + 1) in the same step (the first one, 再来一局)
  if (m.phase === 'playing' && (was.phase !== 'playing' || was.game !== m.game)) { play('gstart', { vol: v }); return; }
  if (was.game !== m.game) return;
  if (m.phase === 'playing' && m.moves > was.moves) play(m.kind === 'xiangqi' || m.kind === 'chess' ? 'piece' : 'stone', { vol: v });
  if (m.phase === 'over' && was.phase === 'playing') {
    if (m.moves > was.moves) play(m.kind === 'xiangqi' || m.kind === 'chess' ? 'piece' : 'stone', { vol: v });
    setTimeout(() => play(!m.winner ? 'draw' : !seat ? 'draw' : m.winner === seat ? 'win' : 'lose', { vol: seat ? 1 : 0.6 }), 180);
  }
}
function pictionary(m) {
  const was = G; G = { phase: m.phase, round: m.round, guessed: new Set(m.guessed ?? []) };
  if (!was) return;
  const mine = myGid();
  if (m.phase === 'drawing' && (was.phase !== 'drawing' || was.round !== m.round)) { play('round'); return; }
  // who got it since the last state (the last one's guess can end the round: the state is already the reveal)
  const fresh = was.phase === 'drawing' && was.round === m.round ? [...G.guessed].filter((id) => !was.guessed.has(id)) : [];
  if (fresh.includes(mine)) play('right'); else if (fresh.length) play('ding');
  if (was.phase === 'drawing' && (m.phase === 'reveal' || m.phase === 'over')) {
    const end = m.why === 'time' ? 'timeup' : 'reveal';
    if (fresh.length) setTimeout(() => play(end), 450); else play(end);
  }
  else if (m.phase === 'over' && was.phase !== 'over') play('win', { vol: 0.8 });
}
function werewolf(m) {
  const was = W; W = { phase: m.phase, voted: (m.voted ?? []).length, day: m.day };
  if (m.phase === 'idle' || (m.phase === 'deal' && was?.phase !== 'deal')) Wrole = null; // a new game: my role comes with it (w_you)
  if (!was || m.phase === was.phase) { if (was && m.phase === 'vote' && W.voted > was.voted) play('tick'); return; }
  if (m.phase === 'night') play('night');
  else if (m.phase === 'dawn' || (m.phase === 'day' && was.phase !== 'dawn')) play('day');
  else if (m.phase === 'vote') play('vote');
  else if (m.phase === 'exile') play('reveal');
  else if (m.phase === 'over' && m.winner && !m.aborted) {
    const r = Wrole?.id === myGid() ? Wrole.role : null;
    play(m.winner === 'draw' ? 'draw' : !r ? 'reveal' : (r === 'wolf') === (m.winner === 'wolves') ? 'win' : 'lose');
  }
}
function spy(m) {
  const was = Sp; Sp = { phase: m.phase, round: m.round, game: m.game, speaker: m.speaker, voted: (m.voted ?? []).length };
  if (!was) return;
  const fresh = m.phase === 'describe' && (was.phase !== 'describe' || was.round !== m.round || was.game !== m.game);
  if (fresh) play('round');
  if (m.phase === 'describe' && m.speaker && m.speaker === myGid() && (fresh || m.speaker !== was.speaker)) setTimeout(() => play('turn'), fresh ? 600 : 0);
  if (m.phase === 'vote' && was.phase !== 'vote') play('vote');
  else if (m.phase === 'vote' && Sp.voted > was.voted) play('tick');
  if (m.phase !== was.phase && (m.phase === 'reveal' || m.phase === 'over')) play('reveal');
  else if (m.phase === 'final' && was.phase !== 'final') play('win', { vol: 0.8 });
}
/** Every message from the office, before the page handles it (app.js onMessage). Never throws. */
function msg(m) {
  try {
    const t = m?.t; if (!t) return;
    if (t === 'welcome') { welcomeAt = performance.now(); tables = new Map(); forgetHuts(); }
    else if (t === 'greet') {
      const now = Date.now(); for (const [k, at] of greetedAt) if (now - at > 10_000) greetedAt.delete(k);
      if (m.from) greetedAt.set(m.from, now); clearTimeout(waves.get(m.from)); waves.delete(m.from); play('greet', { hidden: true });
    }
    else if (t === 'emote') emote(m);
    else if (t === 'chat') { if (m.id !== myId() && performance.now() - welcomeAt > 2000) play('chat', from(o?.peers?.().get(m.id)) ?? { vol: FAR_VOL }); }
    else if (t === 'duo_ask') { const k = m.id ?? `${m.from}:${m.kind}`; if (!asks.has(k)) { asks.add(k); if (asks.size > 50) asks.delete(asks.values().next().value); play('ask', { hidden: true }); } }
    else if (t === 'duo_play') { const me = myId(); if (me && (m.a === me || m.b === me)) play('duo'); }
    else if (t === 'gm_state') gomoku(m);
    else if (t === 'gm_bye') tables.delete(m.table); // walked away from that table: its next state is a fresh look
    else if (t === 'g_state') pictionary(m);
    else if (t === 'w_state') werewolf(m);
    else if (t === 'w_you') Wrole = { id: m.id, role: m.role };
    else if (t === 's_state') spy(m);
  } catch (e) { console.error(e); }
}

// ---------------------------------------------------------------- 设置 → 声音
function section(box) {
  const row = (text) => { const l = document.createElement('label'); l.style.cssText = 'display:flex;gap:6px;align-items:center;font-size:13px;margin:4px 0;cursor:pointer'; l.append(text); return l; };
  const save = () => { try { localStorage.setItem(KEY, JSON.stringify(set)); } catch {} };
  const vol = document.createElement('input'); vol.type = 'range'; vol.min = '0'; vol.max = '100'; vol.step = '5'; vol.value = String(Math.round(set.vol * 100));
  vol.style.cssText = 'flex:1;min-width:0;accent-color:#1b1410'; vol.setAttribute('aria-label', t('app.sounds.volume'));
  const pct = document.createElement('span'); pct.style.cssText = 'width:3em;text-align:right;font-variant-numeric:tabular-nums'; pct.textContent = vol.value + '%';
  vol.oninput = () => { set.vol = +vol.value / 100; pct.textContent = vol.value + '%'; if (master) master.gain.setTargetAtTime(set.vol, ctx.currentTime, 0.02); save(); };
  vol.onchange = () => { last.delete('greet'); play('greet', { preview: true }); }; // let go: hear how loud it is now
  const l1 = row(document.createTextNode(t('app.sounds.volumeLabel'))); l1.style.cursor = ''; l1.append(vol, pct);
  const box2 = (k, text, sample) => {
    const cb = document.createElement('input'); cb.type = 'checkbox'; cb.checked = set[k];
    cb.onchange = () => { set[k] = cb.checked; save(); if (cb.checked) { last.delete(sample); play(sample, { preview: true }); } };
    const l = row(cb); l.append(document.createTextNode(text)); return l;
  };
  const hint = document.createElement('p'); hint.style.cssText = 'font-size:12px;color:#666';
  hint.textContent = t('app.sounds.hint');
  box.append(l1, box2('notify', t('app.sounds.notify'), 'greet'), box2('game', t('app.sounds.game'), 'stone'), hint);
}

/**
 * Hook the sounds up (app.js, once): o {me, peers, statusOf, zoneAt, addSettingsSection, off (): the 扬声器 section's 关闭声音 is on}.
 * @returns {{msg(m): void}}
 */
export function initSounds(opts) {
  o = opts;
  setInterval(watchZone, 1000);
  opts.addSettingsSection?.({ id: 'sound', title: () => t('app.sounds.title'), icon: 'speaker', order: 12.5, render: section });
  if (navigator.userActivation?.hasBeenActive) unlock();
  return { msg };
}
/** An invite card to me (members.js): a 会议室 rings, the rest chime. Sounds with the tab hidden too. */
export function inviteSound(ring) { return play(ring ? 'ring' : 'invite', { hidden: true }); }
/** For a quick listen (and the previews): every sound by name. */
export const sounds = { play: (name, opts) => play(name, opts), unlock, get settings() { return { ...set }; },
  /** A hook for tests and recordings: onPlay(name, gain, pan) as each sound starts. */
  onPlay: null };
